/**
 * GlbOverlay (A5) — visionneuse GLB en canvas transparent superposé à la zone
 * figée, avec mise à l'échelle / rotation / position directes. Vanilla THREE
 * (pas de R3F — cf. useThreeViewer.js) ; caméra fixe cohérente avec la vue
 * figée (plongée ~62°). Le conteneur est draggable pour caler le modèle.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { Move, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GlbOverlay = ({ project, onConfigSaved }) => {
  const canvasRef = useRef(null);
  const modelRef = useRef(null);
  const cfg0 = project.glb_config || { scale: 1, heading_deg: 0, altitude_m: 0 };
  const [scale, setScale] = useState(cfg0.scale || 1);
  const [heading, setHeading] = useState(cfg0.heading_deg || 0);
  const [box, setBox] = useState({ x: window.innerWidth / 2 - 220, y: 160, w: 440, h: 340 });
  const drag = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(box.w, box.h, false);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, box.w / box.h, 0.1, 2000);
    camera.position.set(10, 12, 10);           // plongée ≈ vue figée (tilt ~62°)
    camera.lookAt(0, 0, 0);
    scene.add(new THREE.AmbientLight(0xffffff, 0.85));
    const sun = new THREE.DirectionalLight(0xfff3e0, 1.4);
    sun.position.set(8, 14, 6);
    scene.add(sun);

    const loader = new GLTFLoader();
    loader.load(`${API}/map-projects/${project.map_project_id}/glb`, (gltf) => {
      const model = gltf.scene;
      const bb = new THREE.Box3().setFromObject(model);
      const size = bb.getSize(new THREE.Vector3());
      const norm = 8 / Math.max(size.x, size.y, size.z, 0.001);
      model.scale.setScalar(norm);
      const center = bb.getCenter(new THREE.Vector3()).multiplyScalar(norm);
      model.position.sub(center);
      model.position.y += (size.y * norm) / 2;
      const holder = new THREE.Group();
      holder.add(model);
      scene.add(holder);
      modelRef.current = { holder, norm };
    }, undefined, () => { /* échec de chargement — canvas vide */ });

    let raf = 0;
    const animate = () => {
      const m = modelRef.current;
      if (m) {
        m.holder.scale.setScalar(scaleRef.current);
        m.holder.rotation.y = (headingRef.current * Math.PI) / 180;
      }
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => { cancelAnimationFrame(raf); renderer.dispose(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.map_project_id, box.w, box.h]);

  const scaleRef = useRef(scale); scaleRef.current = scale;
  const headingRef = useRef(heading); headingRef.current = heading;

  const onDown = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, x: box.x, y: box.y };
    const move = (ev) => setBox((b) => ({ ...b, x: drag.current.x + ev.clientX - drag.current.sx, y: drag.current.y + ev.clientY - drag.current.sy }));
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const save = async () => {
    await axios.patch(`${API}/map-projects/${project.map_project_id}`,
      { glb_config: { scale, heading_deg: heading, screen: box } }, { withCredentials: true });
    onConfigSaved?.();
  };

  return (
    <div className="absolute z-[505]" style={{ left: box.x, top: box.y, width: box.w }}
         data-testid="glb-overlay">
      <div className="flex items-center gap-2 px-2 h-8 rounded-t-lg cursor-grab active:cursor-grabbing select-none"
           style={{ background: 'rgba(10,10,13,0.9)', border: '1px solid rgba(94,234,212,0.4)', borderBottom: 'none' }}
           onPointerDown={onDown}>
        <Move className="w-3 h-3 text-teal-300" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-teal-300">Modèle 3D</span>
        <label className="text-[9px] text-gray-400 ml-1">Échelle</label>
        <input type="range" min={0.2} max={4} step={0.05} value={scale}
               onChange={(e) => setScale(parseFloat(e.target.value))}
               onPointerDown={(e) => e.stopPropagation()}
               className="w-20 accent-teal-400" data-testid="glb-scale-slider" />
        <label className="text-[9px] text-gray-400">Rot.</label>
        <input type="range" min={0} max={360} step={1} value={heading}
               onChange={(e) => setHeading(parseInt(e.target.value, 10))}
               onPointerDown={(e) => e.stopPropagation()}
               className="w-16 accent-teal-400" data-testid="glb-heading-slider" />
        <button type="button" onClick={save} className="ml-auto" title="Enregistrer le calage"
                data-testid="glb-save-btn">
          <Save className="w-3.5 h-3.5 text-teal-300 hover:text-white" />
        </button>
      </div>
      <canvas ref={canvasRef} width={box.w} height={box.h}
              className="block rounded-b-lg"
              style={{ border: '1px solid rgba(94,234,212,0.4)', borderTop: 'none', background: 'transparent' }} />
    </div>
  );
};
